import { computed, toValue, type MaybeRefOrGetter } from "vue";

import CotreatmentDashboardService from "@/api/models/treatmentPlan/cotreatmentDashboard";
import type {
  ICooperatePracticeOptionFilters,
  ICotreatmentDashboardFilters,
  TCooperatePracticeOption,
  TCotreatmentDashboardItem,
} from "@/api/models/treatmentPlan/cotreatmentDashboard";
import type { ECooperatePartiesType } from "@/api/models/treatmentPlan/job";
import { ECotreatmentTabs, type TFilter } from "@/types/filter/tableFilters";

import {
  createQueryKeys,
  EFilterType,
  useGenericListQuery,
  useGenericPaginatedQuery,
  type TFilterFieldMap,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";
import {
  ACTIVE_PLAN_STATES,
  ARCHIVED_PLAN_STATES,
  useTreatmentPlanActionableCounts,
} from "@/composables/models/treatmentPlan/useTreatmentPlan.ts";

export const CotreatmentDashboardQueryKeys = createQueryKeys(
  CotreatmentDashboardService.queryKey,
  (key) => ({
    dashboard: key<ICotreatmentDashboardFilters>("cotreatment-dashboard"),
    cooperatePractices: key<ICooperatePracticeOptionFilters>([
      "cotreatment-dashboard",
      "cooperate-practices",
    ]),
  })
);

/**
 * Option list for the Assigned GP/Ortho Office filters. `type` narrows the list
 * to one link type (`gp`, `ortho`, `owner`, `referral_recivier`).
 *
 */
export const useCooperatePracticeOptions = (
  type: MaybeRefOrGetter<ECooperatePartiesType | undefined>
) => {
  const filters = computed<Partial<ICooperatePracticeOptionFilters>>(() => {
    const value = toValue(type);

    return value ? { type: [value] } : {};
  });

  return useGenericListQuery<TCooperatePracticeOption, Partial<ICooperatePracticeOptionFilters>>({
    queryKey: CotreatmentDashboardQueryKeys.cooperatePractices(),
    params: { filters },
    fetcher: (filters) => CotreatmentDashboardService.cooperatePractices({}, filters),
  });
};

/**
 * UI filter fields for the cotreatment dashboard.
 *
 * Both practice selects are single-value in the UI but list-valued on the API, which is what
 * `SELECT_LIST` means.
 */
export const cotreatmentDashboardFields = {
  assignedGpPractice: { type: EFilterType.SELECT_LIST, field: "assigned_gp_practice" },
  assignedOrthoPractice: { type: EFilterType.SELECT_LIST, field: "assigned_ortho_practice" },
  lastUpdate: { type: EFilterType.DATE_RANGE, field: "last_update" },
} satisfies TFilterFieldMap;

export const useCotreatmentDashboard = (
  params: TOwnedFilterParams<ICotreatmentDashboardFilters> = {}
) =>
  useGenericPaginatedQuery<
    TCotreatmentDashboardItem,
    Partial<ICotreatmentDashboardFilters>,
    typeof cotreatmentDashboardFields
  >({
    queryKey: CotreatmentDashboardQueryKeys.dashboard(),
    params: { ...params, fields: cotreatmentDashboardFields },
    fetcher: (fetchParams) =>
      CotreatmentDashboardService.dashboard({}, fetchParams.filters, fetchParams.pageParam),
  });

export { ECotreatmentTabs } from "@/types/filter/tableFilters";

/**
 * The first three tabs are driven by `has_actionable_jobs`:
 * - Actions Needed → `true`
 * - In Progress    → `false`
 * - In Treatment   → omitted entirely (`undefined`), i.e. every running plan
 *
 * Archived instead ignores `has_actionable_jobs` and switches `state` over to the finished
 * plans. Every tab pins `state`, because omitting it returns archived and running plans
 * mixed together — so "In Treatment" means every *active* plan, not literally everything.
 *
 * Labels are derived from the tab keys by `TableFilterTabs`
 * (`actions-needed` → `models.patient.table.predefinedFilters.actionsNeeded`).
 */
export const useCotreatmentTabFilters = () => {
  const { data: actionableCounts } = useTreatmentPlanActionableCounts();

  return computed<TFilter<ECotreatmentTabs, ICotreatmentDashboardFilters>[]>(() => [
    {
      tab: ECotreatmentTabs.ACTIONS_NEEDED,
      badgeNumber: actionableCounts.value?.cotreatment,
      badgeColor: "#808080",

      filters: {
        has_actionable_jobs: true,
        state: ACTIVE_PLAN_STATES,
      },
    },
    {
      tab: ECotreatmentTabs.IN_PROGRESS,
      filters: {
        has_actionable_jobs: false,
        state: ACTIVE_PLAN_STATES,
      },
    },
    {
      tab: ECotreatmentTabs.IN_TREATMENT,
      filters: {
        has_actionable_jobs: undefined,
        state: ACTIVE_PLAN_STATES,
      },
    },
    {
      tab: ECotreatmentTabs.ARCHIVED,
      filters: {
        has_actionable_jobs: undefined,
        state: ARCHIVED_PLAN_STATES,
      },
    },
  ]);
};
